import React, { useCallback, useEffect } from 'react';
import { AppStateUser, useAppState } from '../../../client/providers/AppProvider';
import {
    ConversationCardFragment,
    Espin_Message_Type_Enum,
    Espin_Role_Enum,
    MessagesCardFragment,
} from '../../../generated/spin-graphql';
import { ConversationCardContainer } from './ConversationCardContainer';
import { gql } from '@urql/core';
import { Inbox } from './Inbox';
import { useMessagesContext } from '../../../pages/dashboard/messages';
import { InitialMessageContent } from './InitialConversationContent';
import { InitialConversationCard } from './InitialConversationCard';
import { SearchIcon } from '../../svg/SearchIcon';
import { InputField } from '../../input/InputField';
import { CloseIcon } from '../../svg/CloseIcon';
import { UnsubbedContainer } from '../UnsubbedContainer';
import { ConversationCard } from './ConversationCard';
import { useRouter } from 'next/router';
import { stripHTMLForPreview } from '../../../shared/shared.utils';

type Props = {
    user: AppStateUser;
    initialMessageContent: InitialMessageContent;
    asyncMessageSearch: (searchTerm: string) => Promise<any>;
    adminMessages: MessagesCardFragment[];
};

gql`
    fragment InboxContainer on espin_message {
        ...ConversationCard
    }
`;

export const InboxContainer: React.FC<Props> = ({ user, initialMessageContent, asyncMessageSearch, adminMessages }) => {
    const { subscribed } = useAppState();
    const router = useRouter();
    const {
        messagesDispatch,
        messagesState: { filteredConversationList, searchTermString, selectedIndex },
    } = useMessagesContext();

    const getFakeCards = useCallback(() => {
        const fakeCards: JSX.Element[] = [];
        const filteredConversationKeys = Object.keys(filteredConversationList);

        for (let i = 0; i < Math.min(10, filteredConversationKeys.length); ++i) {
            if (
                !!filteredConversationList[filteredConversationKeys[i]][
                    filteredConversationList[filteredConversationKeys[i]].length - 1
                ]
            ) {
                let fakeConv: ConversationCardFragment = {
                    id: `Message ${i + 1}`,
                    message_type:
                        user.role === Espin_Role_Enum.EspinCollegeUser
                            ? Espin_Message_Type_Enum.PlayerMessagesCollege
                            : Espin_Message_Type_Enum.CollegeMessagesPlayer,
                    player_user_id: `Message ${i + 1}`,
                    contents: 'Upgrade to Premium to see your messages!',
                    created_at:
                        filteredConversationList[filteredConversationKeys[i]][
                            filteredConversationList[filteredConversationKeys[i]].length - 1
                        ].created_at,
                    college_id:
                        filteredConversationList[filteredConversationKeys[i]][
                            filteredConversationList[filteredConversationKeys[i]].length - 1
                        ].college_id,
                    player_user:
                        filteredConversationList[filteredConversationKeys[i]][
                            filteredConversationList[filteredConversationKeys[i]].length - 1
                        ].player_user,
                    college:
                        filteredConversationList[filteredConversationKeys[i]][
                            filteredConversationList[filteredConversationKeys[i]].length - 1
                        ].college,
                };

                const element = (
                    <ConversationCardContainer
                        key={`fake-profile-view-card-${i}`}
                        lastMessage={fakeConv}
                        index={i}
                        isFake={true}
                    />
                );
                fakeCards.push(element);
            }
        }

        return fakeCards;
    }, [filteredConversationList, user?.role]);

    const handleSearchKeyPress = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                asyncMessageSearch(searchTermString);
            }
        },
        [asyncMessageSearch, searchTermString],
    );

    const showInitialConvoCard =
        !!initialMessageContent &&
        selectedIndex === -1 &&
        searchTermString === '' &&
        !!!router.query.show_admin_messages;
    const hasAdminMessage = adminMessages.length > 0;

    return (
        <div
            className={`inbox-container${
                !!initialMessageContent || selectedIndex > 0 || router.query.show_admin_messages === 'true'
                    ? ' has-conversation'
                    : ''
            }`}
        >
            <div className="header-container">
                <SearchIcon />
                <InputField
                    className="messages-search"
                    label="Search messages..."
                    placeholder="Search messages..."
                    htmlFor="search-messages"
                    required={false}
                    handleChange={(searchTerm) => messagesDispatch({ type: 'setSearchTermString', searchTerm })}
                    value={searchTermString}
                    onKeyPress={handleSearchKeyPress}
                />
                {searchTermString.length > 0 && (
                    <button onClick={() => messagesDispatch({ type: 'setSearchTermString', searchTerm: '' })}>
                        <CloseIcon />
                    </button>
                )}
            </div>
            <div className="content">
                {hasAdminMessage && (
                    <div className="admin-conversation-container">
                        <ConversationCard
                            slug={`/dashboard/messages/?show_admin_messages=true`}
                            lastMessage={stripHTMLForPreview(adminMessages[adminMessages.length - 1].contents)}
                            nameLabel={'Stay Plugged In'}
                            date={adminMessages[adminMessages.length - 1].created_at}
                            picture={null}
                            selected={router.query.show_admin_messages === 'true'}
                            read={adminMessages[adminMessages.length - 1].viewed_at}
                        />
                    </div>
                )}
                <div className="conversation-grid">
                    {showInitialConvoCard && <InitialConversationCard initialMessageContent={initialMessageContent} />}
                    {filteredConversationList.length > 0 &&
                        (subscribed ? (
                            <Inbox conversations={filteredConversationList} selectedIndex={selectedIndex} />
                        ) : (
                            <>{getFakeCards()}</>
                        ))}
                    {subscribed &&
                        filteredConversationList.length === 0 &&
                        !!!initialMessageContent &&
                        router.query.show_admin_messages !== 'true' && (
                            <div className="no-conversation-container">
                                <h2 className="title">{'No conversations found'}</h2>
                                <p className="subtitle">
                                    Start a conversation from any
                                    {user.role === Espin_Role_Enum.EspinCollegeUser ? ' player ' : ' college '}
                                    profile.
                                </p>
                            </div>
                        )}
                    {!subscribed && (
                        <UnsubbedContainer
                            title="Unlock Messages"
                            subtitle="Upgrade to Premium to see and send messages!"
                            ctaLabel="GET PREMIUM"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
